import { ReferralBmiProvenance, ReferralPropagationState } from './referral';
export interface DocumentListItem {
  documentId: number;
  documentUuid: string;
  externalId?: string;
  source: DocumentSource;
  receivingClinicId?: number;
  documentCreatedAt: string;
  s3Key: string;
  processingRetryCount: number;
  detectedDocumentId: number;
  documentType: string;
  detectedClinicId?: number;
  confidence: number;
  pageStart: number;
  pageEnd: number;
  patientDocumentId: number;
  patientIdentifier: string;
  patientName: string;
  extractionStatus: ExtractionStatus;
  extractionDate?: string;
  clinicInternalDocumentId?: string;
  processingStatus: DocumentProcessingStatus;
  processingStep: ProcessingStep;
  requiresReview: boolean;
  reviewState: DocumentReviewState;
  /** Null when the document was never classified, so there is no type to have a rule for. */
  isParsingEnabled?: boolean | null;
  extractionFailed: boolean;
  isProcessed: boolean;
  referralId?: number;
  userId?: number;
  referralStatus?: string;
  matchStatus?: string;
  eligibilityStatus?: string;
  priorAuthId?: number;
  paStatus?: string;
  paPlatform?: string;
  priorAuthStatus?: string;
  crmId?: string;
  crmLeadName?: string;
  patientDocumentCreatedAt?: string;
  referralCreatedAt?: string;
  priorAuthCreatedAt?: string;
  lastProcessingAttempt?: string;
  isDuplicate: boolean;
  originalDocumentId?: number | null;
}

export interface DocumentListResponse {
  documents: DocumentListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: DocumentListFilters;
}

/**
 * The operator-facing state of an inbound document, derived server-side in
 * `document_list_view` so the list, the filters and the API cannot disagree about it.
 *
 * Distinct from `DocumentProcessingStatus` (19 values) crossed with `ProcessingStep` (6),
 * which describe *where* a document is in the pipeline. This describes whether anyone needs
 * to do something about it.
 */
export enum DocumentReviewState {
  /** Nothing to do: data extracted and unflagged, resolved by a person, or a duplicate. */
  Parsed = 'parsed',
  /** Something was extracted, but it is flagged or incomplete. */
  NeedsReview = 'needs-review',
  /** Nothing usable was extracted, and the document is no longer moving. */
  Failed = 'failed',
  /** Still in flight. Not one of the brief's three states; see the note on the labels below. */
  Processing = 'processing',
}

export interface DocumentListFilters {
  documentTypes?: string[];
  processingStatuses?: DocumentProcessingStatus[];
  processingSteps?: ProcessingStep[];
  sourceTypes?: DocumentSource[];
  receivingClinicIds?: number[];
  detectedClinicIds?: number[];
  hasReferrals?: boolean;
  hasLinkedUsers?: boolean;
  reviewStates?: DocumentReviewState[];
  requiresReview?: boolean;
  isProcessed?: boolean;
  crmIds?: string[];
  dateFrom?: string;
  dateTo?: string;
}

export interface DocumentListParams extends DocumentListFilters {
  page?: number;
  limit?: number;
  searchDocumentId?: string;
  searchCrmId?: string;
  searchPatientName?: string;
  sortBy?: 'documentCreatedAt' | 'lastProcessingAttempt' | 'processingStatus';
  sortOrder?: 'asc' | 'desc';
}

export interface DocumentDetailResponse {
  // Document core
  documentId: number;
  documentUuid: string;
  externalId: string | null;
  source: DocumentSource;
  receivingClinicId: number | null;
  documentCreatedAt: string;
  s3Key: string | null;
  processingRetryCount: number;
  // Processing summary
  overallProcessingStatus: DocumentProcessingStatus;
  currentProcessingStep: ProcessingStep;
  requiresReview: boolean;
  lastProcessingAttempt: string | null;
  // Detected documents
  detectedDocuments: DetectedDocumentResponse[];
  // Processing history
  processingHistory: ProcessingHistoryResponse[];
  // Processing attempts (detailed)
  processingAttempts: ProcessingAttemptResponse[];
  // Referral documents
  referralDocuments: ReferralDocumentResponse[];
  // Prior authorization documents
  priorAuthDocuments: PriorAuthDocumentResponse[];
}

export interface ProcessingHistoryResponse {
  id: number;
  step: ProcessingStep | null;
  status: DocumentProcessingStatus | null;
  attemptNumber: number;
  retryCount: number;
  processingMetadata: Record<string, any> | null;
  errorMessage: string | null;
  createdAt: string;
}

export interface ProcessingAttemptResponse {
  id: number;
  step: ProcessingStep | null;
  status: DocumentProcessingStatus | null;
  attemptNumber: number;
  contextType: 'document' | 'detected-document' | 'patient';
  contextId: number;
  llmPrompt: string | null;
  llmInput: string | null;
  llmOutput: Record<string, any> | null;
  llmRawResponse: string | null;
  confidence: number | null;
  errorMessage: string | null;
  processingMetadata: Record<string, any> | null;
  createdAt: string;
}

export interface DetectedDocumentResponse {
  id: number;
  documentType: string;
  clinicId: number | null;
  confidence: number | null;
  pageStart: number;
  pageEnd: number;
  extractedData: Record<string, any> | null;
  createdAt: string;
  // Processing attempts for this detected document
  processingAttempts: ProcessingAttemptResponse[];
  // Patient documents within this detected document
  patientDocuments: PatientDocumentResponse[];
}

export interface PatientDocumentResponse {
  id: number;
  patientIdentifier: string | null;
  extractionStatus: ExtractionStatus;
  extractionDate: string | null;
  clinicInternalDocumentId: string | null;
  extractedData: Record<string, any> | null;
  crmMetadata: any | null;
  createdAt: string;
  // Processing attempts for this patient document
  processingAttempts: ProcessingAttemptResponse[];
  // Referral
  referralDocument?: ReferralDocumentResponse;
  // Prior Authorization
  priorAuthDocument?: PriorAuthDocumentResponse;
}

export interface ReferralDocumentResponse extends ReferralBmiProvenance {
  id: number;
  clinicId?: number | null;
  clinicInternalDocumentId?: string | null;
  status: string;
  eligibilityStatus: string;
  demographics?: any | null;
  insurance?: any | null;
  matchSource?: string | null;
  matchStatus?: string | null;
  userId?: number | null;
  enrollmentStatus?: string | null;
  engagementStatus: string;
  enrollmentLinkStatusHistory?: any[] | null;
  /**
   * Cross-service sync state. `propagation` is the per-target delivery ledger for the last
   * edit — typed, unlike the rest of this blob, because the UI reads it.
   */
  syncState?: ({ propagation?: ReferralPropagationState } & Record<string, unknown>) | null;
  /** Set when the referral has been discarded. Discarded referrals stay in the record. */
  discardedAt?: string | null;
  discardReason?: string | null;
  isDuplicate?: boolean;
  duplicateOfReferralId?: number | null;
  duplicateReason?: string | null;
  isCommunityReferral?: boolean;
  /** The stored cohort. `isCommunityReferral` cannot represent a third value. */
  systemSource?: string | null;
  referralSource?: {
    sourceClinicName?: string;
    provider?: {
      fullName?: string;
      address?: string;
      fax?: string;
      office?: string;
      phone?: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface PriorAuthDocumentResponse {
  id: number;
  clinicId?: number | null;
  clinicInternalDocumentId?: string | null;
  status: string;
  paStatus: string;
  paPlatform?: string | null;
  patient?: {
    firstName?: string;
    lastName?: string;
    dateOfBirth?: string;
    patientId?: string;
  } | null;
  provider?: {
    name?: string;
    phone?: string;
    fax?: string;
  } | null;
  medications?:
    | {
        medicationName: string;
        medicationDosage?: string;
        medicationDuration?: string;
        relatedDiagnosisIcd10Codes?: string[];
      }[]
    | null;
  diagnoses?:
    | {
        diagnosisIcd10Code: string;
        diagnosisDescription?: string;
      }[]
    | null;
  coverage?: {
    startDate?: string;
    endDate?: string;
  } | null;
  clinicalRationale?: string | null;
  formSubmissionUrl?: string | null;
  formSubmissionTrackingKey?: string | null;
  documentSubmissionDate?: string | null;
  documentDate?: string | null;
  errorMessage?: string | null;
  createdAt: string;
  updatedAt: string;
  discardedAt?: string | null;
  discardReason?: string | null;
}

export interface DocumentType {
  id: number;
  name: string;
  isParsingEnabled: boolean;
  documentRules: any;
  createdAt: string;
  createdBy: string | null;
  updatedAt: string;
  updatedBy: string | null;
  deletedAt: string | null;
}

export interface DocumentTypeResponse {
  success: boolean;
  documentContents: DocumentType[];
}

export interface LlmPromptResponse {
  id: number;
  name: string;
  documentType: string;
  version: string;
  content: string;
  isActive: boolean;
  modelId: string;
  maxTokens: number;
  temperature: number;
  anthropicVersion: string;
  createdAt: string;
  updatedAt?: string;
}

export interface LlmPromptListResponse {
  prompts: LlmPromptResponse[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export type DetectedDocumentActionMode = 'change-type' | 'discard' | 'discard-patient';

export interface ChangeDetectedDocumentTypeRequest {
  newDocumentType: string;
  reason: string;
  crmNote?: string;
}

export interface ChangeDetectedDocumentTypeResponse {
  detectedDocumentId: number;
  reExtractionJobId: string | null;
  message: string;
}

export interface DiscardDetectedDocumentRequest {
  reason: string;
  crmNote?: string;
}

export interface DiscardDetectedDocumentResponse {
  detectedDocumentId: number;
  message: string;
}

export interface DiscardPatientDocumentRequest {
  reason: string;
  crmNote?: string;
}

export interface DiscardPatientDocumentResponse {
  patientDocumentId: number;
  message: string;
}

export enum PhoneType {
  Cell = 'cell',
  Home = 'home',
  Other = 'other',
  Work = 'work',
}

export enum Gender {
  Male = 'M',
  Female = 'F',
}

export enum InsuranceType {
  Primary = 'primary',
  Secondary = 'secondary',
  Tertiary = 'tertiary',
}
export enum DocumentProcessingStatus {
  Pending = 'pending',
  Classified = 'classified',
  Completed = 'completed',
  CompletedMissingMandatoryFields = 'completed-missing-mandatory-fields',
  CompletedMissingEligibilityFields = 'completed-missing-eligibility-fields',
  Failed = 'failed',
  Retry = 'retry',
}

export enum DocumentSource {
  DrChrono = 'drchrono',
  ReferralForm = 'referral-form',
  Unknown = 'unknown',
  OpsDashboard = 'ops-dashboard',
  OldImportedReferral = 'old-imported-referral',
}

export enum ExtractionStatus {
  Pending = 'pending',
  Processing = 'processing',
  Completed = 'completed',
  Failed = 'failed',
  CompletedClassificationOnly = 'completed-classification-only',
}

export enum ProcessingStep {
  Received = 'received',
  Classification = 'classification',
  Extraction = 'extraction',
  PostProcessing = 'post-processing',
  S3Upload = 'upload-to-s3',
  Validation = 'validation',
}

export enum DocumentHealthStatus {
  Warning = 'warning',
  Error = 'error',
  Success = 'success',
  Info = 'info',
}

export enum UpdateMessageType {
  Success = 'success',
  Error = 'error',
}

export enum AlertType {
  Success = 'success',
  Error = 'error',
  Warning = 'warning',
  Info = 'info',
}

export enum GenderType {
  Male = 'M',
  Female = 'F',
  Other = 'O',
}

export enum PhoneNumberType {
  Cell = 'cell',
  Home = 'home',
  Other = 'other',
  Work = 'work',
}
